import { useEffect, useRef } from 'react';

import {
  trackOnboardingCompleted,
  trackOnboardingStepCompleted,
} from '@/features/setup/helpers/track-onboarding';
import { useOnboardingLadder } from '@/features/setup/hooks/use-onboarding-ladder';
import {
  hasTimestampForStep,
  useUpdateDerivedStepTimestamp,
} from '@/features/setup/hooks/use-update-derived-step-timestamp';
import {
  OnboardingLadderStepId,
} from '@/features/setup/types/onboarding-ladder-enums';
import { useAccountState } from '@/contexts/account/account-context';

enum DERIVED_STEP_SOURCE {
  INTEGRATION = 'integration',
  DESTINATION = 'destination',
}

const DERIVED_STEP_INFO: Record<
  OnboardingLadderStepId,
  { stepLabel: string; stepIndex: number; source: DERIVED_STEP_SOURCE } | undefined
> = {
  // connect_shopify and install_tracking are handled by get-started-page.tsx (first session flow)
  [OnboardingLadderStepId.CONNECT_SHOPIFY]: undefined,
  [OnboardingLadderStepId.CONNECT_FACEBOOK_SOURCE]: {
    stepLabel: 'Connect Meta Ads',
    stepIndex: 3, // 1-based: step 3 of 9
    source: DERIVED_STEP_SOURCE.INTEGRATION,
  },
  [OnboardingLadderStepId.ENABLE_FACEBOOK_DESTINATION]: {
    stepLabel: 'Set up Meta destination',
    stepIndex: 4, // 1-based: step 4 of 9
    source: DERIVED_STEP_SOURCE.DESTINATION,
  },
  [OnboardingLadderStepId.CONNECT_KLAVIYO]: {
    stepLabel: 'Connect Klaviyo',
    stepIndex: 6, // 1-based: step 6 of 9
    source: DERIVED_STEP_SOURCE.DESTINATION,
  },
  [OnboardingLadderStepId.CONNECT_GOOGLE_SOURCE]: {
    stepLabel: 'Connect Google data source',
    stepIndex: 8, // 1-based: step 8 of 9
    source: DERIVED_STEP_SOURCE.INTEGRATION,
  },
  [OnboardingLadderStepId.ENABLE_GOOGLE_DESTINATION]: {
    stepLabel: 'Set up Google destination',
    stepIndex: 9, // 1-based: step 9 of 9
    source: DERIVED_STEP_SOURCE.DESTINATION,
  },
  [OnboardingLadderStepId.INSTALL_TRACKING]: undefined,
  [OnboardingLadderStepId.SHOPIFY_DATA_SHARING_OFF]: undefined,
  [OnboardingLadderStepId.KLAVIYO_FLOWS]: undefined,
};

export function useDerivedStepTracker(): void {
  const { steps, isLoading, isComplete, totalCount, completedCount } = useOnboardingLadder();
  const { currentAccount } = useAccountState();
  const { mutate: updateTimestamp } = useUpdateDerivedStepTimestamp();

  // Track which steps were complete on initial load (seed phase)
  const seededRef = useRef(false);
  const initiallyCompleteRef = useRef<Set<OnboardingLadderStepId>>(new Set());
  const eventsFiredRef = useRef<Set<OnboardingLadderStepId>>(new Set());
  const completedFiredRef = useRef(false);
  const wasCompleteRef = useRef(false);
  const lastAccountIdRef = useRef<string | null>(null);

  useEffect(() => {
    // Don't seed or track while loading or without an account. Don't check isError —
    // cached data is still valid after a refetch failure, and the ladder is built from cache.
    if (isLoading || !currentAccount) return;

    const accountId = currentAccount.id;
    const onboarding = currentAccount.onboarding ?? {};
    const derivedSteps = steps.filter((s) => s.kind === 'derived');

    // Reset all refs on account switch to prevent cross-account pollution
    if (lastAccountIdRef.current !== null && lastAccountIdRef.current !== accountId) {
      seededRef.current = false;
      initiallyCompleteRef.current = new Set();
      eventsFiredRef.current = new Set();
      completedFiredRef.current = false;
      wasCompleteRef.current = false;
    }
    lastAccountIdRef.current = accountId;

    // Seed phase: record steps that are already complete on first load
    if (!seededRef.current) {
      for (const step of derivedSteps) {
        if (step.completed) {
          initiallyCompleteRef.current.add(step.id);
        }
      }
      if (isComplete) {
        wasCompleteRef.current = true;
        completedFiredRef.current = true;
      }
      seededRef.current = true;
      return;
    }

    // Tracking phase: fire analytics for NEW completions, persist timestamps if missing
    for (const step of derivedSteps) {
      if (!step.completed) continue;

      const wasInitiallyComplete = initiallyCompleteRef.current.has(step.id);
      const hasTimestamp = hasTimestampForStep(onboarding, step.id);
      const alreadyFired = eventsFiredRef.current.has(step.id);

      // Persist timestamp if missing (retry on failure — backend is idempotent)
      if (!hasTimestamp) {
        const now = new Date().toISOString();
        updateTimestamp({ stepId: step.id, timestamp: now });
      }

      // Fire analytics only for NEW completions (not initially complete, not already fired)
      if (!wasInitiallyComplete && !alreadyFired) {
        eventsFiredRef.current.add(step.id);
        const stepInfo = DERIVED_STEP_INFO[step.id];
        if (stepInfo) {
          trackOnboardingStepCompleted({
            stepId: step.id,
            stepLabel: stepInfo.stepLabel,
            stepKind: 'derived',
            stepIndex: stepInfo.stepIndex,
            totalSteps: totalCount,
            completedSteps: completedCount,
            timestamp: new Date().toISOString(),
            source: stepInfo.source,
          });
        }
      }
    }

    // Track overall onboarding completion (only on transition to complete)
    if (isComplete && !wasCompleteRef.current && !completedFiredRef.current) {
      const now = new Date().toISOString();
      const completedStepIds = steps
        .filter((s) => s.completed && s.kind !== 'coming_soon')
        .map((s) => s.id);

      trackOnboardingCompleted({
        totalSteps: totalCount,
        stepsCompleted: completedStepIds,
        timestamp: now,
      });
      completedFiredRef.current = true;
    }
    wasCompleteRef.current = isComplete;
  }, [steps, currentAccount, isLoading, updateTimestamp, isComplete, totalCount, completedCount]);
}
